import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth-context";
import { Loader2 } from "lucide-react";
import ProtectedRoute from "./components/ProtectedRoute";
import ConsentGate from "./components/ConsentGate";
import Login from "./pages/Login";
import { TermsPage, PrivacyPage, DmcaPage } from "./pages/Legal";
import ChatPopout from "./pages/ChatPopout";
import DjBoardPopout from "./pages/DjBoardPopout";
import HostEscalations from "./pages/HostEscalations";
import Leaderboard from "./pages/Leaderboard";
import RoomReport, { AccountPrivacy } from "./pages/RoomReport";
import Signup from "./pages/Signup";
import CenterDashboard from "./pages/CenterDashboard";
import RoomCenter from "./pages/RoomCenter";
import Landing from "./pages/Landing";
import RoomPage from "./pages/Room";
import BlackholeSetup from "./pages/BlackholeSetup";
import AdminDashboard from "./pages/AdminDashboard";
import RoomsDirectory from "./pages/RoomsDirectory";
import KeyholderBrief from "./pages/KeyholderBrief";
import { Toaster } from "sonner";
import CookieBanner from "./components/CookieBanner";
import React from "react";

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("[ErrorBoundary]", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{padding:40,color:"#fff",background:"#1a0000",minHeight:"100vh"}}>
          <h1 style={{color:"#ff4444"}}>Something went wrong</h1>
          <pre style={{whiteSpace:"pre-wrap",fontSize:12,marginTop:16}}>{this.state.error?.toString()}\n{this.state.error?.stack}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

// Phase 3.2 — production console scrubbing.
// Enable verbose `[SR]…` logging only when localStorage `sr_dev_mode === "1"`.
// All app-side debug logs should go through `window.__SR_LOG` instead of
// `console.log` so we get a single chokepoint here.
(() => {
  try {
    const dev = typeof window !== "undefined" && window.localStorage?.getItem("sr_dev_mode") === "1";
    window.__SR_DEV__ = !!dev;
    window.__SR_LOG = dev ? console.log.bind(console, "[SR]") : () => {};
    if (!dev) {
      // Suppress accidental `[SR]` prefixed logs that already exist in the codebase.
      const _log = console.log;
      console.log = (...args) => {
        if (typeof args[0] === "string" && args[0].startsWith("[SR]")) return;
        _log(...args);
      };
    }
  } catch { /* */ }
})();

function HomeRoute() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#050508] text-white">
        <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" />
      </div>
    );
  }
  if (user === false) {
    return <Navigate to="/login" replace />;
  }
  return <ConsentGate><CenterDashboard /></ConsentGate>;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<ErrorBoundary><HomeRoute /></ErrorBoundary>} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-username" element={<Navigate to="/login" replace />} />
            <Route path="/forgot-password" element={<Navigate to="/login" replace />} />
            <Route path="/reset-password" element={<Navigate to="/login" replace />} />
            <Route path="/change-password" element={<Navigate to="/login" replace />} />
            <Route path="/signup" element={<Signup />} />
            <Route
              path="/r/:roomId"
              element={<ErrorBoundary><RoomPage /></ErrorBoundary>}
            />
            <Route
              path="/join/:inviteCode"
              element={<ErrorBoundary><RoomPage /></ErrorBoundary>}
            />
            <Route path="/help/blackhole-setup" element={<BlackholeSetup />} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/rooms" element={<RoomsDirectory />} />
            <Route path="/host-dashboard" element={<Navigate to="/" replace />} />
            <Route path="/center/rooms/:roomId" element={<ErrorBoundary><ProtectedRoute><RoomCenter /></ProtectedRoute></ErrorBoundary>} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/keyholder" element={<KeyholderBrief />} />
            <Route path="/keyholder/:slug" element={<KeyholderBrief />} />
            <Route path="/legal/terms" element={<TermsPage />} />
            <Route path="/legal/privacy" element={<PrivacyPage />} />
            <Route path="/legal/dmca" element={<DmcaPage />} />
            <Route path="/r/:roomId/chat-popout" element={<ProtectedRoute><ChatPopout /></ProtectedRoute>} />
            <Route path="/r/:roomId/dj-board" element={<ErrorBoundary><DjBoardPopout /></ErrorBoundary>} />
            <Route
              path="/dashboard/rooms/:roomId/report"
              element={<ProtectedRoute><RoomReport /></ProtectedRoute>}
            />
            <Route
              path="/dashboard/rooms/:roomId/escalations"
              element={<ProtectedRoute><HostEscalations /></ProtectedRoute>}
            />
            <Route
              path="/account/privacy"
              element={<ProtectedRoute><AccountPrivacy /></ProtectedRoute>}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="bottom-center" theme="dark" toastOptions={{ style: { background: "rgba(10,10,15,0.92)", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" } }} />
          <CookieBanner />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
